import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';
import { useEffect, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { tv } from 'tailwind-variants';

import { AvatarSkeleton } from '@/components/Skeleton';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { buttons } from '@/styles/buttons';
import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { AvatarStack } from '@/ui/Avatars/AvatarStack';
import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { WalletAvatar } from '@/ui/Avatars/WalletAvatar';
import { CheckboxCheckedIcon } from '@/ui/Icons/CheckboxCheckedIcon';
import { CheckCircleIcon } from '@/ui/Icons/CheckCircleIcon';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';
import { CopyIcon } from '@/ui/Icons/CopyIcon';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { formatShortAddress } from '@/utils/address';

import type { AddressedWalletFilter, WalletFilter, WalletFilterOption } from './types';
import { toggleWalletFilter } from './utils';

const COPY_FEEDBACK_MS = 2000;
const AVATAR_SIZE = 32;
const COMPACT_AVATAR_SIZE = 24;
const AVATAR_OVERLAP = 10;

const walletSelectorStyles = tv({
  slots: {
    root: ['flex', 'min-w-0', 'shrink-0', 'items-center', 'gap-3'],
    // No surface of its own, so the avatar lines up with the page content below.
    trigger: ['group', 'inline-flex', 'min-w-0', 'items-center', 'gap-3', 'py-2', 'text-left'],
    lines: ['flex', 'min-w-0', 'flex-col', 'items-start', 'gap-1'],
    title: fontStyles({
      variant: 'LABEL_LG',
      class: ['flex', 'items-center', 'gap-1', 'text-token-gray-1600'],
    }),
    subtitle: fontStyles({
      variant: 'CAPTION',
      class: ['flex', 'items-center', 'gap-1', 'text-token-gray-700'],
    }),
    chevron: [
      'transition-transform',
      'duration-150',
      'ease-in-out',
      'group-data-[state=open]:rotate-180',
    ],
    row: ['grid', 'grid-cols-[auto_auto_minmax(0,1fr)_auto]', 'items-center', 'gap-2'],
    checkbox: [
      'flex',
      'h-[18px]',
      'w-[18px]',
      'shrink-0',
      'items-center',
      'justify-center',
      'rounded-xs',
      'border-2',
      'border-solid',
      'border-token-gray-700',
      'text-special-content-button-purple',
    ],
  },
  variants: {
    interactive: {
      true: {
        trigger: [
          'cursor-pointer',
          'rounded-sm',
          'focus-visible:outline-token-gray-500',
          'focus-visible:outline-[3px]',
          'focus-visible:outline-offset-4',
        ],
        title: ['transition-colors', 'group-hover:text-token-gray-900'],
      },
    },
    checked: {
      true: { checkbox: ['border-none', 'bg-token-purple-1000', 'shadow-15'] },
    },
    /** Match the height of the other controls in the sticky row. */
    compact: {
      true: { trigger: ['h-9', 'py-0'] },
    },
  },
});

type Props = {
  walletOptions: WalletFilterOption[];
  selectedWallet: WalletFilter;
  onWalletChange: (wallet: WalletFilter) => void;
  walletTotals?: Record<WalletFilter, number>;
  /** Public portfolio page: nothing to toggle and no wallet is connected. */
  isWatchOnly?: boolean;
  /** Sticky header: single line, smaller avatar, no second line. */
  compact?: boolean;
};

type WalletOptionWithAddress = WalletFilterOption & {
  address: string;
  id: AddressedWalletFilter;
};

function useCopyAddress() {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => setIsCopied(false), COPY_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const copy = (address: string) => {
    navigator.clipboard.writeText(address).then(
      () => setIsCopied(true),
      () => {},
    );
  };

  return { isCopied, copy };
}

function WalletOptionAvatar({
  option,
  size = 16,
  style,
}: {
  option: WalletFilterOption;
  size?: number;
  style?: CSSProperties;
}) {
  if (option.chainId != null) {
    return <ChainAvatar chainId={option.chainId} size={size} style={style} />;
  }

  return option.wallet ? (
    <WalletAvatar wallet={option.wallet} size={size} style={style} />
  ) : (
    <AvatarSkeleton size={size} style={style} />
  );
}

function walletOptionLabel(option: WalletOptionWithAddress) {
  return formatShortAddress(option.address, 6, 4);
}

function CopyAddressButton({ option }: { option: WalletOptionWithAddress }) {
  const { isCopied, copy } = useCopyAddress();

  return (
    <MatchaTooltip>
      <MatchaTooltipTrigger>
        <button
          type="button"
          aria-label={`Copy ${option.label} wallet address`}
          className={buttons({
            size: 'extraSmall',
            color: 'transparent',
            shape: 'rounded',
            iconOnly: true,
            class: ['text-token-gray-700', 'hover:text-token-gray-1600'],
          })}
          onClick={() => copy(option.address)}
        >
          {isCopied ? <CheckCircleIcon /> : <CopyIcon />}
        </button>
      </MatchaTooltipTrigger>
      <MatchaTooltipContent>{isCopied ? 'Copied' : 'Copy address'}</MatchaTooltipContent>
      <MatchaTooltipPopover />
    </MatchaTooltip>
  );
}

function WalletTriggerBody({
  options,
  compact,
  isInteractive,
}: {
  options: WalletOptionWithAddress[];
  compact: boolean;
  isInteractive: boolean;
}) {
  const { lines, title, chevron } = walletSelectorStyles({ interactive: isInteractive });
  const size = compact ? COMPACT_AVATAR_SIZE : AVATAR_SIZE;
  const isMulti = options.length > 1;

  return (
    <>
      {isMulti ? (
        <AvatarStack aria-hidden="true" className="shrink-0" overlap={AVATAR_OVERLAP} size={size}>
          {options.map((option) => (
            <WalletOptionAvatar key={option.id} option={option} size={size} />
          ))}
        </AvatarStack>
      ) : (
        <WalletOptionAvatar option={options[0]} size={size} />
      )}

      <span className={lines()}>
        <span className={title()}>
          {isMulti ? 'Profile' : walletOptionLabel(options[0])}
          {isInteractive && <ChevronDownSmallIcon className={chevron()} />}
          {/* Interactive implies several wallets, so this never nests in a button. */}
          {!isMulti && <CopyAddressButton option={options[0]} />}
        </span>
      </span>
    </>
  );
}

/**
 * The row is already the checkbox control, so the nested copy button must not
 * reach it. Radix selects a menu item on click, and also on a pointer-up whose
 * pointer-down it never saw, so all three events need stopping.
 */
const stopRowToggle = (event: SyntheticEvent) => event.stopPropagation();

function WalletMenuRow({
  balance,
  isChecked,
  onToggle,
  option,
}: {
  balance?: string;
  isChecked: boolean;
  onToggle: () => void;
  option: WalletOptionWithAddress;
}) {
  const { checkbox, row } = walletSelectorStyles();
  const { item } = popover();

  return (
    <DropdownMenuCheckboxItem
      aria-label={`${option.label} wallet ${walletOptionLabel(option)}`}
      checked={isChecked}
      className={item({ class: row() })}
      onCheckedChange={onToggle}
      onSelect={(event) => event.preventDefault()}
    >
      <span aria-hidden="true" className={checkbox({ checked: isChecked })}>
        {isChecked && <CheckboxCheckedIcon width={10} />}
      </span>
      <WalletOptionAvatar option={option} />
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate">{walletOptionLabel(option)}</span>
        <span
          className="flex"
          onClick={stopRowToggle}
          onPointerDown={stopRowToggle}
          onPointerUp={stopRowToggle}
        >
          <CopyAddressButton option={option} />
        </span>
      </span>
      {balance != null && <span className="justify-self-end text-token-gray-1600">{balance}</span>}
    </DropdownMenuCheckboxItem>
  );
}

export function PortfolioWalletSelector({
  walletOptions,
  selectedWallet,
  onWalletChange,
  walletTotals,
  isWatchOnly = false,
  compact = false,
}: Props) {
  const { formatFiat } = useFiatDisplay();
  const { root, trigger } = walletSelectorStyles();
  const { base, itemsLabel } = popover();

  const addressedOptions = walletOptions.filter(
    (option): option is WalletOptionWithAddress => option.id !== 'all' && option.address != null,
  );

  if (addressedOptions.length === 0) return null;

  // A single wallet has nothing to toggle, and a watched address is not ours to filter.
  if (isWatchOnly || addressedOptions.length === 1) {
    return (
      <div className={root()}>
        <div className={trigger({ compact })}>
          <WalletTriggerBody compact={compact} isInteractive={false} options={addressedOptions} />
        </div>
      </div>
    );
  }

  return (
    <div className={root()}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className={trigger({ compact, interactive: true })}>
          <WalletTriggerBody compact={compact} isInteractive options={addressedOptions} />
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent align="start" className={base({ class: 'min-w-[280px]' })}>
            <DropdownMenuLabel className={itemsLabel({ class: 'text-token-gray-700' })}>
              Show wallets
            </DropdownMenuLabel>
            {addressedOptions.map((option) => (
              <WalletMenuRow
                balance={walletTotals && formatFiat(walletTotals[option.id])}
                isChecked={selectedWallet === 'all' || selectedWallet === option.id}
                key={option.id}
                onToggle={() => onWalletChange(toggleWalletFilter(selectedWallet, option.id))}
                option={option}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>
  );
}
